import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../lib/auth';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Checkbox } from '../components/ui/checkbox';
import { Switch } from '../components/ui/switch';
import { toast } from 'sonner';
import TrialBanner from '../components/TrialBanner';
import PullToRefresh from '../components/PullToRefresh';
import { 
  DollarSign, FileText, Clock, CreditCard, Building, 
  AlertCircle, CheckCircle, Send, Download, Plus, Edit, 
  Trash2, Loader2, TrendingUp, ArrowRight, ArrowDown, ArrowUp,
  Wallet, Scale, RefreshCw, Search, Filter, Sparkles,
  ChevronRight, Calendar, Users, Briefcase, BarChart3,
  BanknoteIcon, Receipt, PiggyBank, Calculator, Landmark,
  AlertTriangle, Eye, Settings, Zap, Star, ExternalLink
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Invoice status config
const invoiceStatusConfig = {
  draft: { label: 'Draft', color: 'bg-slate-100 text-slate-700', icon: FileText },
  pending: { label: 'Pending', color: 'bg-yellow-100 text-yellow-700', icon: Clock },
  sent: { label: 'Sent', color: 'bg-blue-100 text-blue-700', icon: Send },
  viewed: { label: 'Viewed', color: 'bg-cyan-100 text-cyan-700', icon: Eye },
  partial: { label: 'Partial', color: 'bg-orange-100 text-orange-700', icon: ArrowDown },
  paid: { label: 'Paid', color: 'bg-green-100 text-green-700', icon: CheckCircle },
  overdue: { label: 'Overdue', color: 'bg-red-100 text-red-700', icon: AlertCircle },
  void: { label: 'Void', color: 'bg-slate-200 text-slate-500', icon: Trash2 }
};

// Payment processor config with colors and icons
const paymentProcessorConfig = {
  stripe: { name: 'Stripe', icon: CreditCard, color: 'bg-indigo-100 text-indigo-700', description: 'Credit cards, debit cards, ACH' },
  paypal: { name: 'PayPal', icon: Wallet, color: 'bg-blue-100 text-blue-700', description: 'PayPal balance & linked cards' },
  lawpay: { name: 'LawPay', icon: Scale, color: 'bg-emerald-100 text-emerald-700', description: 'Legal-specific processing' },
  affirm: { name: 'Affirm', icon: Calendar, color: 'bg-purple-100 text-purple-700', description: 'Buy now, pay later' }
};

// Account types
const accountTypeConfig = {
  operating: { label: 'Operating', color: 'bg-blue-100 text-blue-700', icon: Building },
  iolta: { label: 'IOLTA/Trust', color: 'bg-emerald-100 text-emerald-700', icon: PiggyBank }
};

export default function BillingAIPage({ embedded = false }) {
  const { token, user } = useAuth();
  const [activeTab, setActiveTab] = useState('dashboard');
  const [loading, setLoading] = useState(true);
  
  // Dashboard data
  const [dashboard, setDashboard] = useState(null);
  
  // Invoices
  const [invoices, setInvoices] = useState([]);
  const [invoiceFilter, setInvoiceFilter] = useState('all');
  
  // Time entries
  const [timeEntries, setTimeEntries] = useState([]);
  const [unbilledStats, setUnbilledStats] = useState({ hours: 0, value: 0 });
  
  // Trust accounts
  const [trustAccounts, setTrustAccounts] = useState([]);
  const [selectedAccount, setSelectedAccount] = useState(null);
  
  // Reconciliations
  const [reconciliations, setReconciliations] = useState([]);
  
  // Billing preferences
  const [preferences, setPreferences] = useState(null);
  
  // Payment processors
  const [paymentProcessors, setPaymentProcessors] = useState([]);
  const [processorLoading, setProcessorLoading] = useState({});
  
  // AI Review
  const [aiReviewResults, setAiReviewResults] = useState(null);
  const [runningAIReview, setRunningAIReview] = useState(false);
  
  // Dialogs
  const [showInvoiceDialog, setShowInvoiceDialog] = useState(false);
  const [showAccountDialog, setShowAccountDialog] = useState(false);
  const [showTimeEntryDialog, setShowTimeEntryDialog] = useState(false);
  const [showTransferDialog, setShowTransferDialog] = useState(false);
  const [showReconcileDialog, setShowReconcileDialog] = useState(false);
  const [showPreferencesDialog, setShowPreferencesDialog] = useState(false);
  const [showPaymentDialog, setShowPaymentDialog] = useState(false);
  const [selectedInvoiceForPayment, setSelectedInvoiceForPayment] = useState(null);
  const [selectedPaymentProcessor, setSelectedPaymentProcessor] = useState(null);
  
  // Saving state
  const [saving, setSaving] = useState(false);
  const [processingPayment, setProcessingPayment] = useState(false);

  // Fetch dashboard
  const fetchDashboard = useCallback(async () => {
    try {
      const res = await fetch(`${API_URL}/api/billing/dashboard`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (res.ok) {
        setDashboard(await res.json());
      }
    } catch (error) {
      console.error('Error fetching dashboard:', error);
    }
  }, [token]);

  // Fetch invoices
  const fetchInvoices = useCallback(async () => {
    try {
      const query = invoiceFilter !== 'all' ? `?status=${invoiceFilter}` : '';
      const res = await fetch(`${API_URL}/api/billing/invoices${query}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (res.ok) {
        const data = await res.json();
        setInvoices(data.invoices || []);
      }
    } catch (error) {
      console.error('Error fetching invoices:', error);
    }
  }, [token, invoiceFilter]);

  // Fetch time entries
  const fetchTimeEntries = useCallback(async () => {
    try {
      const res = await fetch(`${API_URL}/api/billing/time-entries?billable_only=true`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (res.ok) {
        const data = await res.json();
        setTimeEntries(data.entries || []);
        setUnbilledStats({
          hours: data.billable_hours || 0,
          value: data.total_value || 0
        });
      }
    } catch (error) {
      console.error('Error fetching time entries:', error);
    }
  }, [token]);

  // Fetch trust accounts
  const fetchTrustAccounts = useCallback(async () => {
    try {
      const res = await fetch(`${API_URL}/api/billing/accounts`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (res.ok) {
        const data = await res.json();
        setTrustAccounts(data.accounts || []);
      }
    } catch (error) {
      console.error('Error fetching accounts:', error);
    }
  }, [token]);

  // Fetch reconciliations
  const fetchReconciliations = useCallback(async () => {
    try {
      const res = await fetch(`${API_URL}/api/billing/reconciliations`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (res.ok) {
        const data = await res.json();
        setReconciliations(data.reconciliations || []);
      }
    } catch (error) {
      console.error('Error fetching reconciliations:', error);
    }
  }, [token]);

  // Fetch preferences
  const fetchPreferences = useCallback(async () => {
    try {
      const res = await fetch(`${API_URL}/api/billing/preferences`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (res.ok) {
        setPreferences(await res.json());
      }
    } catch (error) {
      console.error('Error fetching preferences:', error);
    }
  }, [token]);

  // Fetch payment processors status
  const fetchPaymentProcessors = useCallback(async () => {
    try {
      const res = await fetch(`${API_URL}/api/billing/payment-processors/status`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (res.ok) {
        const data = await res.json();
        setPaymentProcessors(data.processors || []);
      }
    } catch (error) {
      console.error('Error fetching payment processors:', error);
    }
  }, [token]);

  // Toggle payment processor
  const togglePaymentProcessor = async (processor, enabled) => {
    setProcessorLoading(prev => ({ ...prev, [processor]: true }));
    try {
      const formData = new FormData();
      formData.append('enabled', enabled);
      
      const res = await fetch(`${API_URL}/api/billing/payment-processors/${processor}/toggle`, {
        method: 'PUT',
        headers: { 'Authorization': `Bearer ${token}` },
        body: formData
      });
      
      if (res.ok) {
        toast.success(`${paymentProcessorConfig[processor]?.name} ${enabled ? 'enabled' : 'disabled'}`);
        await fetchPaymentProcessors();
      } else {
        toast.error('Failed to update processor');
      }
    } catch (error) {
      toast.error('Failed to update processor');
    } finally {
      setProcessorLoading(prev => ({ ...prev, [processor]: false }));
    }
  };

  // Set default payment processor
  const setDefaultProcessor = async (processor) => {
    try {
      const formData = new FormData();
      formData.append('processor', processor);
      
      const res = await fetch(`${API_URL}/api/billing/payment-processors/default`, {
        method: 'PUT',
        headers: { 'Authorization': `Bearer ${token}` },
        body: formData
      });
      
      if (res.ok) {
        toast.success(`Default processor set to ${paymentProcessorConfig[processor]?.name}`);
        await fetchPreferences();
      }
    } catch (error) {
      toast.error('Failed to set default processor');
    }
  };

  // Process payment
  const processPayment = async (invoice, processor) => {
    setProcessingPayment(true);
    try {
      // Step 1: Create payment
      const createRes = await fetch(`${API_URL}/api/billing/invoices/${invoice.id}/pay/${processor}`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` },
        body: new FormData()
      });
      
      if (!createRes.ok) {
        const error = await createRes.json();
        throw new Error(error.detail || 'Failed to create payment');
      }
      
      const createData = await createRes.json();
      
      // Step 2: Complete/capture payment (for mocked processors, this always succeeds)
      let completeUrl = '';
      const completeForm = new FormData();
      
      if (processor === 'stripe') {
        // For Stripe, we'd normally use Stripe.js client-side
        // For demo, simulate successful payment
        completeUrl = `${API_URL}/api/billing/invoices/${invoice.id}/pay/stripe/confirm`;
        completeForm.append('payment_intent_id', createData.payment_intent_id);
      } else if (processor === 'paypal') {
        completeUrl = `${API_URL}/api/billing/invoices/${invoice.id}/pay/paypal/capture`;
        completeForm.append('order_id', createData.order_id);
      } else if (processor === 'lawpay') {
        completeUrl = `${API_URL}/api/billing/invoices/${invoice.id}/pay/lawpay/complete`;
        completeForm.append('transaction_id', createData.transaction_id);
      } else if (processor === 'affirm') {
        completeUrl = `${API_URL}/api/billing/invoices/${invoice.id}/pay/affirm/complete`;
        completeForm.append('checkout_token', createData.checkout_token);
      }
      
      const completeRes = await fetch(completeUrl, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` },
        body: completeForm
      });
      
      if (!completeRes.ok) {
        const error = await completeRes.json();
        throw new Error(error.detail || 'Failed to complete payment');
      }
      
      const completeData = await completeRes.json();
      
      if (completeData.success) {
        toast.success(`Payment of ${formatCurrency(invoice.balance_due)} processed successfully!`);
        if (completeData.is_mocked) {
          toast.info('Note: This is a simulated payment (MOCKED)');
        }
        setShowPaymentDialog(false);
        setSelectedInvoiceForPayment(null);
        await fetchInvoices();
        await fetchDashboard();
      } else {
        throw new Error(completeData.message || 'Payment failed');
      }
    } catch (error) {
      toast.error(error.message || 'Payment failed');
    } finally {
      setProcessingPayment(false);
    }
  };

  // Initial fetch
  useEffect(() => {
    const loadData = async () => {
      setLoading(true);
      await Promise.all([
        fetchDashboard(),
        fetchInvoices(),
        fetchTrustAccounts(),
        fetchPreferences(),
        fetchPaymentProcessors()
      ]);
      setLoading(false);
    };
    loadData();
  }, [fetchDashboard, fetchInvoices, fetchTrustAccounts, fetchPreferences, fetchPaymentProcessors]);

  // Refetch when tab changes
  useEffect(() => {
    if (activeTab === 'time') fetchTimeEntries();
    if (activeTab === 'trust') fetchTrustAccounts();
    if (activeTab === 'reconcile') fetchReconciliations();
  }, [activeTab, fetchTimeEntries, fetchTrustAccounts, fetchReconciliations]);

  // Run AI review
  const runAIReview = async () => {
    setRunningAIReview(true);
    try {
      const formData = new FormData();
      formData.append('review_type', 'errors');
      
      const res = await fetch(`${API_URL}/api/billing/ai-review`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` },
        body: formData
      });
      
      if (res.ok) {
        const data = await res.json();
        setAiReviewResults(data);
        if (data.issues?.length > 0) {
          toast.warning(`Found ${data.issues.length} issue(s) in time entries`);
        } else {
          toast.success('No issues found in time entries');
        }
      }
    } catch (error) {
      toast.error('AI review failed');
    } finally {
      setRunningAIReview(false);
    }
  };

  // Format currency
  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format(amount || 0);
  };

  // Refresh handler for pull-to-refresh
  const handleRefresh = useCallback(async () => {
    await Promise.all([
      fetchDashboard(),
      fetchInvoices(),
      fetchTrustAccounts(),
      fetchPreferences(),
      fetchPaymentProcessors()
    ]);
    toast.success('Data refreshed');
  }, [fetchDashboard, fetchInvoices, fetchTrustAccounts, fetchPreferences, fetchPaymentProcessors]);

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-slate-50 to-white flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-slate-400" />
      </div>
    );
  }

  const billingBody = (
    <>
        {embedded && (
          <div className="flex items-center justify-between mb-4">
            <div>
              <h3 className="text-lg font-heading font-semibold">Billing & Invoicing</h3>
              <p className="text-sm text-muted-foreground">Comprehensive legal billing & trust accounting</p>
            </div>
            <div className="flex items-center gap-2">
              <Button variant="outline" size="sm" onClick={() => setShowPreferencesDialog(true)}>
                <Scale className="w-4 h-4 mr-1" /> Settings
              </Button>
              <Button size="sm" onClick={() => setShowInvoiceDialog(true)} data-testid="create-invoice-btn">
                <Plus className="w-4 h-4 mr-1" /> New Invoice
              </Button>
            </div>
          </div>
        )}

      <div className={embedded ? "" : "max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4 sm:py-6"}>
        <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-4 sm:space-y-6">
          <div className="overflow-x-auto -mx-4 px-4 sm:mx-0 sm:px-0">
            <TabsList className="inline-flex w-max sm:grid sm:w-full sm:grid-cols-6 sm:max-w-3xl">
              <TabsTrigger value="dashboard" className="text-xs sm:text-sm" data-testid="tab-dashboard">Dashboard</TabsTrigger>
              <TabsTrigger value="invoices" className="text-xs sm:text-sm" data-testid="tab-invoices">Invoices</TabsTrigger>
              <TabsTrigger value="time" className="text-xs sm:text-sm" data-testid="tab-time">Time & Fees</TabsTrigger>
              <TabsTrigger value="trust" className="text-xs sm:text-sm" data-testid="tab-trust">Trust</TabsTrigger>
              <TabsTrigger value="reconcile" className="text-xs sm:text-sm" data-testid="tab-reconcile">Reconcile</TabsTrigger>
              <TabsTrigger value="ai" className="text-xs sm:text-sm" data-testid="tab-ai">AI Review</TabsTrigger>
            </TabsList>
          </div>

          {/* Dashboard Tab */}
          <TabsContent value="dashboard" className="space-y-6">
            {/* KPI Cards */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
              <Card>
                <CardContent className="pt-6">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm text-slate-500">Outstanding</p>
                      <p className="text-2xl font-bold text-slate-900">
                        {formatCurrency(dashboard?.total_outstanding)}
                      </p>
                      <p className="text-xs text-slate-400">
                        {dashboard?.outstanding_invoices || 0} invoices
                      </p>
                    </div>
                    <div className="p-3 bg-blue-100 rounded-full">
                      <FileText className="w-6 h-6 text-blue-600" />
                    </div>
                  </div>
                </CardContent>
              </Card>

              <Card>
                <CardContent className="pt-6">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm text-slate-500">Overdue</p>
                      <p className="text-2xl font-bold text-red-600">
                        {formatCurrency(dashboard?.total_overdue)}
                      </p>
                      <p className="text-xs text-slate-400">
                        {dashboard?.overdue_invoices || 0} invoices
                      </p>
                    </div>
                    <div className="p-3 bg-red-100 rounded-full">
                      <AlertCircle className="w-6 h-6 text-red-600" />
                    </div>
                  </div>
                </CardContent>
              </Card>

              <Card>
                <CardContent className="pt-6">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm text-slate-500">Unbilled Work</p>
                      <p className="text-2xl font-bold text-slate-900">
                        {formatCurrency(dashboard?.unbilled_value)}
                      </p>
                      <p className="text-xs text-slate-400">
                        {dashboard?.unbilled_hours?.toFixed(1) || 0} hours
                      </p>
                    </div>
                    <div className="p-3 bg-amber-100 rounded-full">
                      <Clock className="w-6 h-6 text-amber-600" />
                    </div>
                  </div>
                </CardContent>
              </Card>

              <Card>
                <CardContent className="pt-6">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm text-slate-500">Trust Balance</p>
                      <p className="text-2xl font-bold text-emerald-600">
                        {formatCurrency(dashboard?.total_trust_balance)}
                      </p>
                      <p className="text-xs text-slate-400">
                        IOLTA accounts
                      </p>
                    </div>
                    <div className="p-3 bg-emerald-100 rounded-full">
                      <PiggyBank className="w-6 h-6 text-emerald-600" />
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>

            {/* Monthly Collections */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              <Card>
                <CardHeader>
                  <CardTitle className="text-lg flex items-center gap-2">
                    <TrendingUp className="w-5 h-5 text-green-600" />
                    This Month
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="text-3xl font-bold text-green-600">
                    {formatCurrency(dashboard?.monthly_collections)}
                  </div>
                  <p className="text-sm text-slate-500 mt-1">Collections received</p>
                </CardContent>
              </Card>

              <Card>
                <CardHeader>
                  <CardTitle className="text-lg">Recent Invoices</CardTitle>
                </CardHeader>
                <CardContent>
                  {dashboard?.recent_invoices?.length > 0 ? (
                    <div className="space-y-2">
                      {dashboard.recent_invoices.slice(0, 4).map(inv => (
                        <div key={inv.id} className="flex items-center justify-between p-2 bg-slate-50 rounded">
                          <div>
                            <span className="font-medium text-sm">{inv.invoice_number}</span>
                            <span className="text-xs text-slate-500 ml-2">{inv.issue_date}</span>
                          </div>
                          <div className="flex items-center gap-2">
                            <span className="font-medium">{formatCurrency(inv.total)}</span>
                            <Badge variant="outline" className={invoiceStatusConfig[inv.status]?.color || ''}>
                              {invoiceStatusConfig[inv.status]?.label || inv.status}
                            </Badge>
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <p className="text-slate-500 text-center py-4">No recent invoices</p>
                  )}
                </CardContent>
              </Card>
            </div>
          </TabsContent>

          {/* Invoices Tab */}
          <TabsContent value="invoices" className="space-y-6">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-4">
                <Select value={invoiceFilter} onValueChange={setInvoiceFilter}>
                  <SelectTrigger className="w-40">
                    <SelectValue placeholder="Filter by status" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Invoices</SelectItem>
                    <SelectItem value="draft">Draft</SelectItem>
                    <SelectItem value="sent">Sent</SelectItem>
                    <SelectItem value="partial">Partial</SelectItem>
                    <SelectItem value="overdue">Overdue</SelectItem>
                    <SelectItem value="paid">Paid</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <Button onClick={() => setShowInvoiceDialog(true)}>
                <Plus className="w-4 h-4 mr-1" />
                New Invoice
              </Button>
            </div>

            <div className="space-y-3">
              {invoices.length > 0 ? invoices.map(invoice => {
                const StatusIcon = invoiceStatusConfig[invoice.status]?.icon || FileText;
                return (
                  <Card key={invoice.id}>
                    <CardContent className="p-4">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-4">
                          <div className={`p-2 rounded-lg ${invoiceStatusConfig[invoice.status]?.color || 'bg-slate-100'}`}>
                            <StatusIcon className="w-5 h-5" />
                          </div>
                          <div>
                            <div className="flex items-center gap-2">
                              <span className="font-semibold">{invoice.invoice_number}</span>
                              <Badge variant="outline" className={invoiceStatusConfig[invoice.status]?.color}>
                                {invoiceStatusConfig[invoice.status]?.label}
                              </Badge>
                            </div>
                            <p className="text-sm text-slate-500">
                              Issued: {invoice.issue_date} • Due: {invoice.due_date}
                            </p>
                          </div>
                        </div>
                        <div className="text-right">
                          <p className="text-lg font-bold">{formatCurrency(invoice.total)}</p>
                          {invoice.balance_due > 0 && invoice.balance_due !== invoice.total && (
                            <p className="text-sm text-slate-500">
                              Balance: {formatCurrency(invoice.balance_due)}
                            </p>
                          )}
                        </div>
                        <div className="flex items-center gap-2 ml-4">
                          <Button variant="ghost" size="sm">
                            <Eye className="w-4 h-4" />
                          </Button>
                          {invoice.status === 'draft' && (
                            <Button variant="ghost" size="sm">
                              <Send className="w-4 h-4" />
                            </Button>
                          )}
                          {invoice.balance_due > 0 && invoice.status !== 'draft' && invoice.status !== 'paid' && (
                            <Button 
                              size="sm" 
                              className="bg-emerald-600 hover:bg-emerald-700"
                              onClick={() => {
                                setSelectedInvoiceForPayment(invoice);
                                setShowPaymentDialog(true);
                              }}
                              data-testid={`pay-invoice-${invoice.id}`}
                            >
                              <CreditCard className="w-4 h-4 mr-1" />
                              Pay
                            </Button>
                          )}
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                );
              }) : (
                <Card>
                  <CardContent className="p-12 text-center">
                    <FileText className="w-16 h-16 mx-auto text-slate-300 mb-4" />
                    <h3 className="text-lg font-medium text-slate-900">No invoices yet</h3>
                    <p className="text-slate-500 mb-4">Create your first invoice to get started</p>
                    <Button onClick={() => setShowInvoiceDialog(true)}>
                      <Plus className="w-4 h-4 mr-1" />
                      Create Invoice
                    </Button>
                  </CardContent>
                </Card>
              )}
            </div>
          </TabsContent>

          {/* Time & Fees Tab */}
          <TabsContent value="time" className="space-y-6">
            <div className="flex items-center justify-between">
              <div>
                <h2 className="text-lg font-semibold">Time Entries & Billable Fees</h2>
                <p className="text-sm text-slate-500">
                  {unbilledStats.hours.toFixed(1)} unbilled hours worth {formatCurrency(unbilledStats.value)}
                </p>
              </div>
              <div className="flex items-center gap-2">
                <Button variant="outline" onClick={runAIReview} disabled={runningAIReview}>
                  {runningAIReview ? (
                    <Loader2 className="w-4 h-4 mr-1 animate-spin" />
                  ) : (
                    <Sparkles className="w-4 h-4 mr-1" />
                  )}
                  AI Review
                </Button>
                <Button onClick={() => setShowTimeEntryDialog(true)}>
                  <Plus className="w-4 h-4 mr-1" />
                  Add Time
                </Button>
              </div>
            </div>

            {aiReviewResults?.issues?.length > 0 && (
              <Card className="border-amber-200 bg-amber-50">
                <CardHeader className="pb-2">
                  <CardTitle className="text-base flex items-center gap-2 text-amber-800">
                    <AlertTriangle className="w-5 h-5" />
                    AI Found {aiReviewResults.issues.length} Issue(s)
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-2">
                    {aiReviewResults.issues.slice(0, 5).map((issue, idx) => (
                      <div key={idx} className="p-2 bg-white rounded border border-amber-200">
                        <div className="flex items-start justify-between">
                          <div>
                            <Badge variant="outline" className={
                              issue.severity === 'error' ? 'bg-red-100 text-red-700' :
                              issue.severity === 'warning' ? 'bg-amber-100 text-amber-700' :
                              'bg-blue-100 text-blue-700'
                            }>
                              {issue.issue_type?.replace(/_/g, ' ')}
                            </Badge>
                            <p className="text-sm mt-1">{issue.explanation}</p>
                          </div>
                          {issue.suggested_value && (
                            <span className="text-xs text-green-600">
                              Suggested: {issue.suggested_value}
                            </span>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            )}

            <div className="space-y-2">
              {timeEntries.slice(0, 20).map(entry => (
                <Card key={entry.id}>
                  <CardContent className="p-3">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <div className="p-2 bg-slate-100 rounded">
                          <Clock className="w-4 h-4 text-slate-600" />
                        </div>
                        <div>
                          <p className="font-medium text-sm">{entry.description}</p>
                          <p className="text-xs text-slate-500">
                            {entry.date} • {entry.user_name || 'Unknown'}
                          </p>
                        </div>
                      </div>
                      <div className="flex items-center gap-4">
                        <div className="text-right">
                          <p className="font-medium">{entry.hours}h × {formatCurrency(entry.hourly_rate)}/hr</p>
                          <p className="text-sm text-slate-500">
                            {formatCurrency(entry.hours * entry.hourly_rate)}
                          </p>
                        </div>
                        <Badge variant="outline" className={
                          entry.status === 'billed' ? 'bg-green-100 text-green-700' :
                          entry.status === 'approved' ? 'bg-blue-100 text-blue-700' :
                          'bg-slate-100 text-slate-700'
                        }>
                          {entry.status}
                        </Badge>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
              {timeEntries.length === 0 && (
                <Card>
                  <CardContent className="p-8 text-center">
                    <Clock className="w-12 h-12 mx-auto text-slate-300 mb-3" />
                    <p className="text-slate-500">No time entries found</p>
                  </CardContent>
                </Card>
              )}
            </div>
          </TabsContent>

          {/* Trust Accounts Tab */}
          <TabsContent value="trust" className="space-y-6">
            <div className="flex items-center justify-between">
              <div>
                <h2 className="text-lg font-semibold">Trust Accounts</h2>
                <p className="text-sm text-slate-500">Manage IOLTA and Operating accounts</p>
              </div>
              <div className="flex items-center gap-2">
                <Button variant="outline" onClick={() => setShowTransferDialog(true)}>
                  <ArrowRight className="w-4 h-4 mr-1" />
                  Transfer
                </Button>
                <Button onClick={() => setShowAccountDialog(true)}>
                  <Plus className="w-4 h-4 mr-1" />
                  Add Account
                </Button>
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {trustAccounts.map(account => {
                const typeConfig = accountTypeConfig[account.account_type] || accountTypeConfig.operating;
                const TypeIcon = typeConfig.icon;
                return (
                  <Card key={account.id} className="hover:shadow-md transition-shadow cursor-pointer"
                    onClick={() => setSelectedAccount(account)}>
                    <CardContent className="p-4">
                      <div className="flex items-start justify-between">
                        <div className="flex items-center gap-3">
                          <div className={`p-2 rounded-lg ${typeConfig.color}`}>
                            <TypeIcon className="w-5 h-5" />
                          </div>
                          <div>
                            <h3 className="font-semibold">{account.name}</h3>
                            <p className="text-sm text-slate-500">{account.bank_name} •••{account.account_number_last4}</p>
                          </div>
                        </div>
                        <Badge variant="outline" className={typeConfig.color}>
                          {typeConfig.label}
                        </Badge>
                      </div>
                      <div className="mt-4 pt-4 border-t">
                        <div className="flex items-center justify-between">
                          <span className="text-sm text-slate-500">Current Balance</span>
                          <span className="text-xl font-bold">{formatCurrency(account.current_balance)}</span>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                );
              })}
              {trustAccounts.length === 0 && (
                <Card className="col-span-2">
                  <CardContent className="p-12 text-center">
                    <Landmark className="w-16 h-16 mx-auto text-slate-300 mb-4" />
                    <h3 className="text-lg font-medium text-slate-900">No accounts configured</h3>
                    <p className="text-slate-500 mb-4">Add your IOLTA and Operating accounts</p>
                    <Button onClick={() => setShowAccountDialog(true)}>
                      <Plus className="w-4 h-4 mr-1" />
                      Add Account
                    </Button>
                  </CardContent>
                </Card>
              )}
            </div>
          </TabsContent>

          {/* Reconciliation Tab */}
          <TabsContent value="reconcile" className="space-y-6">
            <div className="flex items-center justify-between">
              <div>
                <h2 className="text-lg font-semibold">IOLTA Reconciliation</h2>
                <p className="text-sm text-slate-500">Monthly trust account reconciliation</p>
              </div>
              <Button onClick={() => setShowReconcileDialog(true)}>
                <Calculator className="w-4 h-4 mr-1" />
                New Reconciliation
              </Button>
            </div>

            <div className="space-y-3">
              {reconciliations.map(rec => (
                <Card key={rec.id}>
                  <CardContent className="p-4">
                    <div className="flex items-center justify-between">
                      <div>
                        <div className="flex items-center gap-2">
                          <span className="font-medium">
                            {rec.period_start} to {rec.period_end}
                          </span>
                          <Badge variant="outline" className={
                            rec.status === 'completed' ? 'bg-green-100 text-green-700' :
                            rec.status === 'discrepancy' ? 'bg-red-100 text-red-700' :
                            'bg-yellow-100 text-yellow-700'
                          }>
                            {rec.status}
                          </Badge>
                        </div>
                        <p className="text-sm text-slate-500">
                          Statement: {formatCurrency(rec.statement_ending_balance)} • 
                          Book: {formatCurrency(rec.book_balance)}
                        </p>
                      </div>
                      {rec.difference !== 0 && (
                        <div className="text-right">
                          <p className="text-sm text-slate-500">Difference</p>
                          <p className="font-bold text-red-600">{formatCurrency(rec.difference)}</p>
                        </div>
                      )}
                    </div>
                  </CardContent>
                </Card>
              ))}
              {reconciliations.length === 0 && (
                <Card>
                  <CardContent className="p-8 text-center">
                    <RefreshCw className="w-12 h-12 mx-auto text-slate-300 mb-3" />
                    <p className="text-slate-500">No reconciliations yet</p>
                  </CardContent>
                </Card>
              )}
            </div>
          </TabsContent>

          {/* AI Review Tab */}
          <TabsContent value="ai" className="space-y-6">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Sparkles className="w-5 h-5 text-indigo-600" />
                  AI Billing Assistant
                </CardTitle>
                <CardDescription>
                  Use AI to identify billing errors, optimize time entries, and ensure compliance
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <Card className="border-2 hover:border-indigo-200 cursor-pointer transition-colors">
                    <CardContent className="p-4 text-center">
                      <AlertTriangle className="w-8 h-8 mx-auto text-amber-500 mb-2" />
                      <h3 className="font-medium">Error Detection</h3>
                      <p className="text-xs text-slate-500">Find duplicates, excessive hours, vague descriptions</p>
                    </CardContent>
                  </Card>
                  <Card className="border-2 hover:border-indigo-200 cursor-pointer transition-colors">
                    <CardContent className="p-4 text-center">
                      <TrendingUp className="w-8 h-8 mx-auto text-green-500 mb-2" />
                      <h3 className="font-medium">Optimization</h3>
                      <p className="text-xs text-slate-500">Suggest better descriptions, rate improvements</p>
                    </CardContent>
                  </Card>
                  <Card className="border-2 hover:border-indigo-200 cursor-pointer transition-colors">
                    <CardContent className="p-4 text-center">
                      <Scale className="w-8 h-8 mx-auto text-blue-500 mb-2" />
                      <h3 className="font-medium">Compliance</h3>
                      <p className="text-xs text-slate-500">Check billing guidelines compliance</p>
                    </CardContent>
                  </Card>
                </div>

                <div className="flex justify-center pt-4">
                  <Button size="lg" onClick={runAIReview} disabled={runningAIReview}>
                    {runningAIReview ? (
                      <>
                        <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                        Analyzing Time Entries...
                      </>
                    ) : (
                      <>
                        <Sparkles className="w-5 h-5 mr-2" />
                        Run Full AI Review
                      </>
                    )}
                  </Button>
                </div>

                {aiReviewResults && (
                  <div className="mt-6 p-4 bg-slate-50 rounded-lg">
                    <h4 className="font-medium mb-2">Review Results</h4>
                    <div className="flex items-center gap-4 text-sm">
                      <span>Entries reviewed: {aiReviewResults.entries_reviewed || 0}</span>
                      <span className={aiReviewResults.issues_found > 0 ? 'text-amber-600 font-medium' : 'text-green-600'}>
                        Issues found: {aiReviewResults.issues_found || 0}
                      </span>
                    </div>
                    {aiReviewResults.issues?.length > 0 && (
                      <div className="mt-3 space-y-2">
                        {aiReviewResults.issues.map((issue, idx) => (
                          <div key={idx} className="p-3 bg-white rounded border flex items-start justify-between">
                            <div>
                              <Badge variant="outline" className={
                                issue.severity === 'error' ? 'bg-red-100 text-red-700' :
                                issue.severity === 'warning' ? 'bg-amber-100 text-amber-700' :
                                'bg-blue-100 text-blue-700'
                              }>
                                {issue.issue_type?.replace(/_/g, ' ')}
                              </Badge>
                              <p className="text-sm mt-1">{issue.explanation}</p>
                            </div>
                            <span className="text-xs text-slate-400">
                              {(issue.confidence * 100).toFixed(0)}% confidence
                            </span>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </div>

      {/* Create Invoice Dialog - Placeholder */}
      <Dialog open={showInvoiceDialog} onOpenChange={setShowInvoiceDialog}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>Create Invoice</DialogTitle>
            <DialogDescription>Create a new invoice for a client</DialogDescription>
          </DialogHeader>
          <div className="py-4">
            <p className="text-center text-slate-500">
              Invoice creation form will be implemented with case/client selection,
              time entry and expense selection, and invoice customization.
            </p>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowInvoiceDialog(false)}>Cancel</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Create Account Dialog */}
      <Dialog open={showAccountDialog} onOpenChange={setShowAccountDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Add Trust Account</DialogTitle>
            <DialogDescription>Connect an IOLTA or Operating account</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div>
              <Label>Account Name</Label>
              <Input placeholder="e.g., Client Trust Account" />
            </div>
            <div>
              <Label>Account Type</Label>
              <Select>
                <SelectTrigger>
                  <SelectValue placeholder="Select type" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="iolta">IOLTA/Trust Account</SelectItem>
                  <SelectItem value="operating">Operating Account</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Bank Name</Label>
              <Input placeholder="e.g., Chase Bank" />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Account Number (last 4)</Label>
                <Input placeholder="1234" maxLength={4} />
              </div>
              <div>
                <Label>Opening Balance</Label>
                <Input type="number" placeholder="0.00" />
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowAccountDialog(false)}>Cancel</Button>
            <Button>Add Account</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Preferences Dialog - With Payment Processor Management */}
      <Dialog open={showPreferencesDialog} onOpenChange={setShowPreferencesDialog}>
        <DialogContent className="max-w-3xl max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Settings className="w-5 h-5" />
              Billing Settings
            </DialogTitle>
            <DialogDescription>Configure your firm's billing preferences and payment processors</DialogDescription>
          </DialogHeader>
          
          <div className="space-y-6 py-4">
            {/* General Settings */}
            <div>
              <h3 className="font-semibold mb-3 flex items-center gap-2">
                <BarChart3 className="w-4 h-4" />
                General Settings
              </h3>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Default Hourly Rate</Label>
                  <Input type="number" defaultValue={preferences?.default_hourly_rate || 350} />
                </div>
                <div>
                  <Label>Payment Terms (days)</Label>
                  <Input type="number" defaultValue={preferences?.default_payment_terms || 30} />
                </div>
              </div>
              <div className="mt-3">
                <Label>Invoice Prefix</Label>
                <Input defaultValue={preferences?.invoice_prefix || 'INV-'} className="max-w-xs" />
              </div>
            </div>

            {/* Payment Processors Section */}
            <div className="border-t pt-4">
              <h3 className="font-semibold mb-3 flex items-center gap-2">
                <CreditCard className="w-4 h-4" />
                Payment Processors
              </h3>
              <p className="text-sm text-slate-500 mb-4">
                Enable payment processors clients can use to pay invoices. Toggle on/off and set your default.
              </p>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                {paymentProcessors.map(pp => {
                  const config = paymentProcessorConfig[pp.processor] || {};
                  const ProcessorIcon = config.icon || CreditCard;
                  const isDefault = preferences?.default_payment_processor === pp.processor;
                  
                  return (
                    <Card key={pp.processor} className={`${pp.enabled ? 'border-emerald-200' : 'border-slate-200'} transition-colors`}>
                      <CardContent className="p-4">
                        <div className="flex items-start justify-between">
                          <div className="flex items-center gap-3">
                            <div className={`p-2 rounded-lg ${config.color || 'bg-slate-100 text-slate-700'}`}>
                              <ProcessorIcon className="w-5 h-5" />
                            </div>
                            <div>
                              <div className="flex items-center gap-2">
                                <span className="font-semibold">{pp.name}</span>
                                {pp.is_mocked && (
                                  <Badge variant="outline" className="bg-amber-50 text-amber-700 text-xs">
                                    MOCKED
                                  </Badge>
                                )}
                                {isDefault && (
                                  <Badge variant="outline" className="bg-blue-50 text-blue-700 text-xs">
                                    <Star className="w-3 h-3 mr-1" />
                                    Default
                                  </Badge>
                                )}
                              </div>
                              <p className="text-xs text-slate-500">{pp.description}</p>
                            </div>
                          </div>
                          <Switch
                            checked={pp.enabled}
                            onCheckedChange={(checked) => togglePaymentProcessor(pp.processor, checked)}
                            disabled={processorLoading[pp.processor]}
                          />
                        </div>
                        
                        {pp.enabled && (
                          <div className="mt-3 pt-3 border-t flex items-center justify-between">
                            <div className="flex items-center gap-2 text-xs text-slate-500">
                              {pp.supports_cards && <Badge variant="outline" className="text-xs">Cards</Badge>}
                              {pp.supports_ach && <Badge variant="outline" className="text-xs">ACH</Badge>}
                              {pp.supports_bnpl && <Badge variant="outline" className="text-xs">BNPL</Badge>}
                            </div>
                            {!isDefault && (
                              <Button 
                                variant="ghost" 
                                size="sm"
                                onClick={() => setDefaultProcessor(pp.processor)}
                              >
                                Set as Default
                              </Button>
                            )}
                          </div>
                        )}
                      </CardContent>
                    </Card>
                  );
                })}
              </div>
            </div>

            {/* State Bar Format */}
            <div className="border-t pt-4">
              <h3 className="font-semibold mb-3">State Bar Report Format</h3>
              <Select defaultValue={preferences?.state_bar_report_format || 'standard'}>
                <SelectTrigger className="max-w-xs">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="standard">Standard Format</SelectItem>
                  <SelectItem value="california">California Format</SelectItem>
                  <SelectItem value="new_york">New York Format</SelectItem>
                  <SelectItem value="texas">Texas Format</SelectItem>
                  <SelectItem value="florida">Florida Format</SelectItem>
                  <SelectItem value="custom">Custom Format</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowPreferencesDialog(false)}>Close</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Payment Dialog */}
      <Dialog open={showPaymentDialog} onOpenChange={(open) => {
        if (!open) {
          setSelectedInvoiceForPayment(null);
          setSelectedPaymentProcessor(null);
        }
        setShowPaymentDialog(open);
      }}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <CreditCard className="w-5 h-5 text-emerald-600" />
              Pay Invoice
            </DialogTitle>
            <DialogDescription>
              Select a payment method to pay invoice {selectedInvoiceForPayment?.invoice_number}
            </DialogDescription>
          </DialogHeader>
          
          {selectedInvoiceForPayment && (
            <div className="space-y-4 py-4">
              {/* Invoice Summary */}
              <Card className="bg-slate-50">
                <CardContent className="p-4">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm text-slate-500">Amount Due</p>
                      <p className="text-2xl font-bold text-slate-900">
                        {formatCurrency(selectedInvoiceForPayment.balance_due)}
                      </p>
                    </div>
                    <div className="text-right">
                      <p className="font-medium">{selectedInvoiceForPayment.invoice_number}</p>
                      <p className="text-sm text-slate-500">Due: {selectedInvoiceForPayment.due_date}</p>
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* Payment Processor Selection */}
              <div>
                <Label className="mb-2 block">Select Payment Method</Label>
                <div className="space-y-2">
                  {paymentProcessors.filter(pp => pp.enabled).map(pp => {
                    const config = paymentProcessorConfig[pp.processor] || {};
                    const ProcessorIcon = config.icon || CreditCard;
                    const isSelected = selectedPaymentProcessor === pp.processor;
                    const requiresSetup = pp.requires_api_key && pp.processor === 'stripe';
                    
                    return (
                      <div
                        key={pp.processor}
                        onClick={() => !requiresSetup && setSelectedPaymentProcessor(pp.processor)}
                        className={`p-4 border-2 rounded-lg transition-all ${
                          requiresSetup 
                            ? 'border-slate-200 bg-slate-50 opacity-60 cursor-not-allowed'
                            : isSelected 
                              ? 'border-emerald-500 bg-emerald-50 cursor-pointer' 
                              : 'border-slate-200 hover:border-slate-300 cursor-pointer'
                        }`}
                        data-testid={`payment-option-${pp.processor}`}
                      >
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-3">
                            <div className={`p-2 rounded-lg ${config.color || 'bg-slate-100'}`}>
                              <ProcessorIcon className="w-5 h-5" />
                            </div>
                            <div>
                              <p className="font-medium">{pp.name}</p>
                              <p className="text-xs text-slate-500">{config.description}</p>
                              {requiresSetup && (
                                <p className="text-xs text-red-500 mt-1">Requires API key configuration</p>
                              )}
                            </div>
                          </div>
                          <div className="flex items-center gap-2">
                            {pp.is_mocked && (
                              <Badge variant="outline" className="bg-amber-50 text-amber-700 text-xs">
                                Demo
                              </Badge>
                            )}
                            {requiresSetup && (
                              <Badge variant="outline" className="bg-red-50 text-red-700 text-xs">
                                Setup Required
                              </Badge>
                            )}
                            {isSelected && !requiresSetup && (
                              <CheckCircle className="w-5 h-5 text-emerald-600" />
                            )}
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
                
                {paymentProcessors.filter(pp => pp.enabled).length === 0 && (
                  <div className="text-center py-8">
                    <AlertCircle className="w-12 h-12 mx-auto text-slate-300 mb-2" />
                    <p className="text-slate-500">No payment processors enabled</p>
                    <p className="text-sm text-slate-400">Enable processors in Settings</p>
                  </div>
                )}
              </div>

              {/* Info about mocked processors */}
              {selectedPaymentProcessor && paymentProcessors.find(p => p.processor === selectedPaymentProcessor)?.is_mocked && (
                <div className="p-3 bg-amber-50 border border-amber-200 rounded-lg">
                  <div className="flex items-start gap-2">
                    <AlertTriangle className="w-4 h-4 text-amber-600 mt-0.5" />
                    <div>
                      <p className="text-sm font-medium text-amber-800">Demo Mode</p>
                      <p className="text-xs text-amber-700">
                        This payment processor is in demo mode. Payments will be simulated and no actual charges will occur.
                      </p>
                    </div>
                  </div>
                </div>
              )}
            </div>
          )}
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowPaymentDialog(false)}>Cancel</Button>
            <Button 
              className="bg-emerald-600 hover:bg-emerald-700"
              disabled={!selectedPaymentProcessor || processingPayment}
              onClick={() => processPayment(selectedInvoiceForPayment, selectedPaymentProcessor)}
              data-testid="confirm-payment-btn"
            >
              {processingPayment ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Processing...
                </>
              ) : (
                <>
                  <Zap className="w-4 h-4 mr-2" />
                  Pay {formatCurrency(selectedInvoiceForPayment?.balance_due)}
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );

  if (embedded) {
    return billingBody;
  }

  return (
    <PullToRefresh onRefresh={handleRefresh} className="min-h-screen bg-gradient-to-b from-slate-50 to-white">
      <div data-testid="billing-ai-page">
        <TrialBanner />
        <div className="bg-white border-b">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-emerald-100 rounded-lg">
                  <DollarSign className="w-6 h-6 text-emerald-700" />
                </div>
                <div>
                  <h1 className="text-xl sm:text-2xl font-bold text-slate-900">Billing.AI</h1>
                  <p className="text-xs sm:text-sm text-slate-500">Comprehensive legal billing & trust accounting</p>
                </div>
              </div>
              <div className="flex items-center gap-2 sm:gap-3">
                <Button variant="outline" size="sm" className="sm:size-default" onClick={() => setShowPreferencesDialog(true)}>
                  <Scale className="w-4 h-4 sm:mr-1" />
                  <span className="hidden sm:inline">Settings</span>
                </Button>
                <Button size="sm" className="sm:size-default" onClick={() => setShowInvoiceDialog(true)} data-testid="create-invoice-btn">
                  <Plus className="w-4 h-4 sm:mr-1" />
                  <span className="hidden sm:inline">New Invoice</span>
                </Button>
              </div>
            </div>
          </div>
        </div>
        {billingBody}
      </div>
    </PullToRefresh>
  );
}
